// Core
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Box, Button, Flex, Text, Grid, Divider, Image, useColorMode, Skeleton, useColorModeValue } from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { getMonthName } from 'services/Utils';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import { ReactComponent as CopyIcon } from 'assets/svg/copy.svg';
import { ReactComponent as CopySuccessIcon } from 'assets/svg/copy-success.svg';
import cube1 from 'assets/img/details/cube-1.png';
import cube2 from 'assets/img/details/cube-2.png';

// Components
import { StarsIcon } from 'components/Icons/Icons';
import Card from 'components/Card/Card';
import FilterSelect from 'components/Form/FilterSelect';

const Referrer = (props) => {
  const history = useHistory();

  useEffect(() => {
    if (props.userData && props.userData.canRefer === false) {
      history.push('/'); 
    }
  }, [props.userData, history]);

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const styleHeader = {
    color: '#099BFF',
    textAlign: 'center',
    fontSize: '18px',
    fontStyle: 'italic',
    fontWeight: '800',
    lineHeight: 'normal',
    textTransform: 'uppercase',
    height: '44px'
  };

  const styleCard = {
    padding: '18px 16px 10px 16px',
    textAlign: 'center',
    position: 'relative',
    border: '1px solid #2ABDFD',
    overflow: 'hidden',
  };

  const bg = useColorModeValue('white', '#0E1633');  
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
  const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
  const dividerCodeDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 12.72%, #FAFDFE 23.59%, #FFF 30.15%, #38A9E1 48.09%, rgba(56, 169, 225, 0.00) 100%)';
  const dividerCodeWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const dividerCode = useColorModeValue(dividerCodeWhite, dividerCodeDark);
  const dividerDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 32.25%, #FFF 50.39%, #38A9E1 70.91%, rgba(56, 169, 225, 0.00) 100%)';
  const dividerWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const divider = useColorModeValue(dividerWhite, dividerDark);

  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
      const text = document.querySelector('.referrer-link')?.textContent; 

      if (text) {
          navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 7000); 
          }).catch(err => console.error('Error copy:', err));
      }
  };

  const { data: dataStats, isFetching: isFetchingStats } = useQuery({
    queryKey: ['referrer-stats'],
    queryFn: async () => {
        const response = await apiService.getReferrerStats();
        return response.data;
    },
    refetchOnWindowFocus: false,
    retry: false
  });

  const { data: dataLink, isFetching: isFetchingLink } = useQuery({
    queryKey: ['referrer-link'],
    queryFn: async () => {
        const response = await apiService.getReferrerLink();
        return response.data;
    },
    refetchOnWindowFocus: false,
    retry: false
  });

  const currentYear = new Date().getFullYear();
  const startYear = 2017;

  const yearOptions = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = currentYear - i;
    return { value: String(year), title: `Year ${year}` };
  });

  const [period, setPeriod] = useState(currentYear);
  const { data: dataHistory, isFetching: isFetchingHistory } = useQuery({
    queryKey: ['referrer-history', period],
    queryFn: async () => {
        const response = await apiService.getReferrerHistory({year: period});
        return response.data;
    },
    refetchOnWindowFocus: false,
    retry: false
  });

  const handlePeriod = (e) => {
    const newPeriod = e.target.value;
    setPeriod(newPeriod);
  };

  return (
  	<Box m={{base: '-40px auto 100px auto', md: '37px auto 100px auto'}}>
  		<Flex gap={{base: '8px', md: '26px'}} m="62px 0 46px 0" w="100%" alignItems="center" textAlign="center" justifyContent="center">
      	<StarsIcon fontSize={{base: '28px', md: '43px'}} />
      	{ isDark && <Box bg="#099BFF"
          className="animation-opacity" 
      		filter="blur(40px)" w="50%" h="40px" left="50%" borderRadius="full"
      		transform="translate(-50%, 40px)" position="absolute" zIndex="-1" />}
      	<Text variant="header" fontSize={{base: '46px', md: '64px', lg: '72px'}}>Referrer</Text>
      	<StarsIcon fontSize={{base: '28px', md: '43px'}} />
      </Flex>


      <Text variant="title-lg" fontSize="32px" textTransform="uppercase" fontWeight="800" mb="24px">Dashboard</Text>

      <Grid mt="18px" rowGap="10px" templateColumns={{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr 1fr 1fr' }} gap="16px" templateRows='1fr'>
        { isFetchingStats ? 
        <>
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
        </>
        :
        <>
          <Card style={styleCard} bg={bg}>
            { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1 }} /> : <LinesWhite className="abstract-lines" style={{ opacity: 0.3, zIndex: 1 }} /> }
            
            <Text style={styleHeader}>Overall signups</Text>
            <Text variant="title-md" py="8px" fontSize="28px" fontStyle="italic" fontWeight="800">
              {dataStats?.overall?.total || 0}
            </Text>
          </Card>

          <Card style={styleCard} bg={bg}>
            { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1 }} /> : <LinesWhite className="abstract-lines" style={{ opacity: 0.3, zIndex: 1 }} /> }

            <Text style={styleHeader}>Overall active</Text>
            <Text pt="8px" variant="title-md" fontSize="28px" fontStyle="italic" fontWeight="800">
              {dataStats?.overall?.active || 0}
            </Text>
          </Card>

          <Card style={styleCard} bg={bg}>
            { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1 }} /> : <LinesWhite className="abstract-lines" style={{ opacity: 0.3, zIndex: 1 }} /> }

            <Text style={styleHeader}>Overall Cancelled</Text>
            <Text pt="8px" variant="title-md" fontSize="28px" fontStyle="italic" fontWeight="800">
              {dataStats?.overall?.canceled || 0}
            </Text>
          </Card>

          <Card style={styleCard} bg={bg}>
            { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1 }} /> : <LinesWhite className="abstract-lines" style={{ opacity: 0.3, zIndex: 1 }} /> }

            <Text style={styleHeader}>Total Free Trial Signups</Text>
            <Text pt="8px" variant="title-md" fontSize="28px" fontStyle="italic" fontWeight="800">
              {dataStats?.freeTrial?.total || 0}
            </Text>
          </Card>

          <Card style={styleCard} bg={bg}>
            { isDark ? <Lines className="abstract-lines" style={{ zIndex: 1 }} /> : <LinesWhite className="abstract-lines" style={{ opacity: 0.3, zIndex: 1 }} /> }

            <Text style={styleHeader}>Total Free Trial Canceled</Text>
            <Text pt="8px" variant="title-md" fontSize="28px" fontStyle="italic" fontWeight="800">
              {dataStats?.freeTrial?.canceled || 0}
            </Text>
          </Card>
        </>
        }
      </Grid>

      { isFetchingLink ? 
        <Skeleton height="320px" mt="24px" borderRadius="16px" />
      :  
      <Card padding={{base: '16px'}} 
        border={border} 
        boxShadow={boxShadow} 
        mt="24px" 
        textAlign="center" 
        bg={bg}
        className="animation-referrer" 
        overflow="hidden">
        <Text m="6px auto 0 auto" variant="header" maxW="550px" lineHeight="52px" fontSize={{base: '36px', md: '48px'}}>
          Your <Text as="span" variant="header-gradient" lineHeight="52px" fontSize={{base: '36px', md: '48px'}}>Personalised Referral</Text> link
        </Text>

        <Card
          zIndex="3" 
          padding="16px" 
          m="18px auto 32px auto" 
          bg={ isDark ? '#0D234D' : '#F5FAFF' }
          textAlign="left" 
          overflow="hidden"
          textAlign="center"
          maxW="530px">

          <Text zIndex="2" variant="title-md">Copy the below link to invite new users to LMCT+</Text>

          <Flex
            zIndex="2"
            mt="16px"
            justifyContent="space-between"
            alignItems="center"
            borderRadius="16px"
            position="relative"
            height="56px"
            fontWeight="800"
            padding="0px 16px"
            overflow="hidden"
            background="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
            boxShadow={ isDark ? '0px 4px 12px 0px rgba(0, 122, 255, 0.50)' : 'unset' }>
            <Text variant="header" fontSize={{base: '12px', md: '16px', lg: '20px'}} className="referrer-link" color="white">
              { dataLink || 'https://lmctplus.com' }
            </Text>

            <Box borderRadius="8px" bg={ isDark ? '#0D234D' : 'white'} cursor="pointer" p="4px" onClick={handleCopy}>
              {copied ? <CopySuccessIcon fontSize="23px" /> : <CopyIcon fontSize="23px" />}
            </Box>

            <Box 
              boxShadow="-110px -1px 13px #ffffff"
              position="absolute" 
              width="100%"
                height="2px"
                left="0"
                bottom="0"
              bg={dividerCode} /> 
          </Flex>

          { isDark ? <Lines className="abstract-lines" style={{ zIndex: '1'}} /> 
            : <LinesWhite className="abstract-lines" style={{ zIndex: '1', opacity: 0.3 }} /> }
        </Card>

        <Box position="absolute" w="100%" bottom="-40px">
          { isDark && <Box 
            position="absolute" 
            zIndex="3"
            bg="linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 39.95%, #FFF 50.39%, #38A9E1 60.59%, rgba(56, 169, 225, 0.00) 100%)" 
            w="100%" h="2px" 
            bottom="40px" /> }
          <Image className="animation-referrer-crystal-box1" left="10%" bottom="0" position="absolute" src={cube1} alt="Cube" maxW="265px" />
          <Image className="animation-referrer-crystal-box2" right="10%" bottom="0" position="absolute" src={cube2} alt="Cube" />
        </Box>
      </Card>
      }

      <Flex mt="90px" alignItems="center" justifyContent="space-between">
        <Text variant="title" fontSize="32px" fontWeight="800" textTransform="uppercase">History</Text>
        <FilterSelect 
          id="filter-select"
          onChange={handlePeriod}
          value={period}
          mb="0"
          bg={ isDark ? 'transparent !important' : null }
          w={{base: '130px', sm: '164px'}}
          options={yearOptions}
        />
      </Flex>

      <Grid mt="32px" rowGap="10px" templateColumns={{ sm: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' }} gap="10px" templateRows='1fr'>
        { isFetchingHistory ? 
        <>
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
          <Skeleton height="125px" borderRadius="16px" />
        </>
        :
        <>
          { dataHistory?.map((item, index) => (
            <Card padding="16px" border={border} key={index} bg={bg}>
              <Text variant="text-sm">{period}</Text>
              <Text variant="title-lg" mt="11px" fontSize="24px" fontWeight="500">{getMonthName(item.month)}</Text>

              <Box h="1px" borderTop={border} m="12px 0 8px 0" />

              <Flex gap="10px" justifyContent="space-between">
                <Box>
                  <Text variant="text-sm" color="#099BFF" fontSize="12px">Signed-up</Text>
                  <Text variant="title-md" fontWeight="700" pt="3px">{item.total}</Text>
                </Box>
                <Box>
                  <Text variant="text-sm" color="#34C759" fontSize="12px">Active</Text>
                  <Text variant="title-md" fontWeight="700" pt="3px">{item.active}</Text>
                </Box>
                <Box>
                  <Text variant="text-sm" color="#FF3B30" fontSize="12px">Cancelled</Text>
                  <Text variant="title-md" fontWeight="700" pt="3px">{item.canceled}</Text>
                </Box>
              </Flex>
            </Card>
          ))} 
        </>
        }
      </Grid>


      
  	</Box>
  );
};

export default Referrer;